// Promoting a private note to a draft of the pull request on screen
// (docs/design.md "Notes", Promotion), and what a note shows of it. The
// plan (logic/promotion.ts) says where the draft goes; one write holds the
// draft and records it on the note (the notes store's `promote`), shown at
// once as the page's own draft write is (`Conversation.promoteNote`).
// Undo discards the draft, which makes the note promotable again.
import type { Note, NotePromotion, ReviewDraft } from '../api/server';
import type { PromotionPlan } from '../logic/promotion';
import { prPath } from '../router';
import type { NotesStore } from './notes.svelte';
import type { Conversation } from './rangeHost';
import { toast } from './toast.svelte';

/** A plan that promotes. */
export type ReadyPlan = Extract<PromotionPlan, { kind: 'ready' }>;

/** How a note shown on a pull request's page is promoted there (NoteCard). */
export interface NotePromote {
  /** How this note is promoted here. */
  plan: PromotionPlan;
  /** Promote it (a `ready` plan). */
  run: () => Promise<unknown>;
  /** Why no draft can be written here now (read-only, drafts off), else null. */
  off: string | null;
}

/** What promoting takes: the pull request's conversation (where the draft shows) and the notes. */
export interface PromoteDeps {
  conversation: Pick<Conversation, 'owner' | 'repo' | 'number' | 'promoteNote' | 'discardDraft'>;
  notes: Pick<NotesStore, 'promote'>;
}

/**
 * Promote `note` as `plan` says. The draft shows at once and the note
 * shows promoted once the server holds both; the notice offers Undo, which
 * discards the draft. A refusal is told as any failed draft write is, and
 * changes nothing. Returns the draft, or null when it was not made.
 */
export async function promote(deps: PromoteDeps, note: Note, plan: ReadyPlan): Promise<ReviewDraft | null> {
  const { conversation, notes } = deps;
  let made: ReviewDraft | null = null;
  const ok = await conversation.promoteNote(note.id, plan.target, plan.body, async () => {
    made = await notes.promote(note.id, { pull: conversation.number, target: plan.target, body: plan.body });
    return made;
  });
  const draft = made as ReviewDraft | null;
  if (!ok || !draft) return null;
  toast.show({
    text: 'Note promoted to a draft',
    action: { label: 'Undo', run: () => void conversation.discardDraft(draft.id, undefined, { quiet: true }) },
  });
  return draft;
}

/**
 * What a note says of its promotion, and where it leads: a held draft to
 * its pull request's Review view, a published one to its conversation.
 * The pull request is named unless it is the one on screen (`number`).
 */
export function promotionText(p: NotePromotion, here: { owner: string; repo: string; number: number | null }): { text: string; href: string } {
  const where = p.pull === here.number ? '' : ` on #${p.pull}`;
  return p.kind === 'draft'
    ? { text: `Promoted to a draft${where}`, href: prPath(here.owner, here.repo, p.pull, 'review') }
    : { text: `Published as a review comment${where}`, href: prPath(here.owner, here.repo, p.pull) };
}
